"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.steelTheme = void 0;
require("@fontsource/roboto/300.css");
require("@fontsource/roboto/400.css");
require("@fontsource/roboto/500.css");
require("@fontsource/roboto/700.css");
const styles_1 = require("@mui/material/styles");
const colors_1 = require("./styles/colors");
const Button_1 = require("./theming/Button");
const CircleStatus_1 = require("./theming/CircleStatus");
const Select_1 = require("./theming/Select");
const ContentCard_1 = require("./theming/ContentCard");
const DataGrid_1 = require("./theming/DataGrid");
const MobileStepper_1 = require("./theming/MobileStepper");
const Switch_1 = require("./theming/Switch");
const Typography_1 = require("./theming/Typography");
exports.steelTheme = (0, styles_1.createTheme)({
    palette: {
        mode: 'light',
        primary: { main: '#009EE0', light: '#FFFFFF', dark: '#00000' },
        secondary: { main: '#00CDFD', light: '#F8F9FA', dark: '#030B42' },
        success: { main: '#34A06E', light: '#C7E1BF', dark: '#1F8600' },
        info: { main: '#FFCC00', light: '#FFEFAD' },
        warning: { main: '#F17105', light: '#FDD2AF' },
        error: { main: '#F05050', light: '#FFD2D1', dark: '#C50C29' },
        grey: {
            800: '#404040',
            700: '#4C4C4C',
            600: '#666666',
            500: '#7F7F7F',
            400: '#999999',
            300: '#B2B2B2',
            200: '#CCCCCC',
            100: '#E5E5E5',
            50: '#F2F2F2',
        },
    },
    gradient: {
        cta: 'linear-gradient(to right bottom, #009ee0, #0081cc, #0064b6, #00479d, #1a2981)',
        background: 'linear-gradient(to right bottom, #1a2981, #142171, #0d1a61, #071351, #030b42)',
        variant: 'linear-gradient(to left top, #6f5ed3, #3481e6, #009cea, #17b3e5, #60c7dd)',
    },
    circleStatus: {
        draft: Object.assign({}, CircleStatus_1.CIRCLE_STATUS.draft),
        ongoing: Object.assign({}, CircleStatus_1.CIRCLE_STATUS.ongoing),
        pendingAction: Object.assign({}, CircleStatus_1.CIRCLE_STATUS.pendingAction),
        alternativeStatus: Object.assign({}, CircleStatus_1.CIRCLE_STATUS.alternativeStatus),
        processed: Object.assign({}, CircleStatus_1.CIRCLE_STATUS.processed),
        deleted: Object.assign({}, CircleStatus_1.CIRCLE_STATUS.deleted),
    },
    typography: {
        fontFamily: 'Roboto',
        h1: { fontStyle: 'normal', fontSize: '32px', fontWeight: 500, letterSpacing: 0 },
        h1Light: { fontFamily: 'Roboto', fontStyle: 'normal', fontSize: '32px', fontWeight: 400, letterSpacing: 0 },
        h2: { fontStyle: 'normal', fontSize: '28px', fontWeight: 300, letterSpacing: 0 },
        h3: { fontStyle: 'normal', fontSize: '24px', fontWeight: 300, letterSpacing: 0 },
        h4: { fontStyle: 'normal', fontSize: '22px', fontWeight: 300, letterSpacing: 0 },
        h5: { fontStyle: 'normal', fontSize: '20px', fontWeight: 300, letterSpacing: 0 },
        h6: { fontStyle: 'normal', fontSize: '18x', fontWeight: 500, letterSpacing: 0 },
        body1: { fontStyle: 'normal', fontSize: '16px', letterSpacing: 0 },
        body2: { fontStyle: 'normal', fontSize: '14px', letterSpacing: 0 },
        subtitle1: { fontStyle: 'normal', fontSize: '16px', letterSpacing: 0 },
        subtitle2: { fontStyle: 'medium', fontSize: '14px', fontWeight: 500, letterSpacing: 0 },
        subtitle3: { fontFamily: 'Roboto', fontStyle: 'medium', fontSize: '12px', fontWeight: 500, letterSpacing: 0 },
        subtitle4: {
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '10px',
            fontWeight: 400,
            letterSpacing: 0,
            color: colors_1.$grey60,
        },
        tableTitle: {
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '14px',
            letterSpacing: 0,
            textTransform: 'uppercase',
        },
        button: {
            fontStyle: 'medium',
            fontSize: '14px',
            fontWeight: 400,
            letterSpacing: 0.02,
            textTransform: 'none',
        },
        buttonMedium: {
            fontStyle: 'normal',
            fontFamily: 'Roboto',
            fontSize: '16px',
            fontWeight: 400,
            textTransform: 'none',
        },
        caption: { fontStyle: 'normal', fontSize: '12px', letterSpacing: 0 },
        overline: {
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '12px',
            letterSpacing: 0.02,
            textTransform: 'uppercase',
        },
        hyperlink1: {
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '16px',
            letterSpacing: 0,
            textDecoration: 'underline',
            color: `${colors_1.$primaryBrand}`,
            cursor: 'pointer',
        },
        hyperlink2: {
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            letterSpacing: 0,
            textDecoration: 'underline',
            color: `${colors_1.$primaryBrand}`,
            cursor: 'pointer',
        },
        hyperlink3: {
            fontFamily: 'Roboto',
            fontStyle: 'regular',
            fontSize: '10px',
            letterSpacing: 0,
            color: `${colors_1.$grey30}`,
            cursor: 'pointer',
        },
        labelForStatus: {
            fontFamily: 'Roboto',
            fontStyle: 'italic',
            fontWeight: 300,
            fontSize: '12px',
            letterSpacing: 0,
            color: `${colors_1.$grey50}`,
        },
        inputLabel: {
            fontFamily: 'Roboto',
            fontWeight: 300,
            fontSize: '10px',
            letterSpacing: 0,
            color: `${colors_1.$grey40}`,
        },
        sectionTitle: {
            fontFamily: 'Roboto',
            fontWeight: 500,
            fontSize: '16px',
            letterSpacing: 0,
        },
        sectionTitleDescription: {
            fontFamily: 'Roboto',
            fontWeight: 300,
            fontSize: '10px',
            letterSpacing: 0,
        },
    },
    shadows: [
        'none',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 2px 4px #03071C24',
        '0px 4px 8px #03071C24',
        '0px 8px 16px #03071C24',
        '0px 16px 32px #03071C24',
    ],
    shape: {
        borderRadius: 6,
    },
    components: {
        MuiTypography: Typography_1.TYPOGRAPHY,
        MuiButton: Button_1.BUTTON,
        MuiSelect: Select_1.SELECT,
        MuiMenu: Select_1.SELECT_DROPDOWN,
        MuiGrid: ContentCard_1.GridLayout,
        MuiCardHeader: ContentCard_1.CardBackground,
        MuiCardContent: ContentCard_1.CardBackground,
        MuiCardActions: ContentCard_1.CardBackground,
        MuiDataGrid: DataGrid_1.DATA_GRID,
        MuiPaper: ContentCard_1.CardBackground,
        MuiDatePicker: ContentCard_1.CardBackground,
        MuiMobileStepper: MobileStepper_1.MOBILE_STEPPER,
        MuiSwitch: Switch_1.SWITCH,
    },
});
exports.default = (0, styles_1.responsiveFontSizes)(exports.steelTheme);
